import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db } from "../Context/firebaseConfig";
import TheNav from "../Components/navbar";
import { collection, getDocs } from "firebase/firestore";
import SEO, { SITE_URL, breadcrumbSchema } from "../Components/SEO";
import DOMPurify from "dompurify";
import { getCategorySlug } from "../utils/categorySlugs";
import { listPublicProfiles } from "../services/publicProfiles";

const MINT = "#10b981";
const PURPLE = "#a855f7";
const BG = "#0a0f0d";
const SURFACE = "#141a17";
const HAIRLINE = "rgba(255,255,255,0.08)";

function formatEditorsCount(count) {
  if (count >= 1000) return (count / 1000).toFixed(1) + "K";
  return count.toString();
}

function getCategoryBadge(categoryName = "") {
  const normalized = categoryName.toLowerCase();

  if (normalized.includes("tiktok") || normalized.includes("clip")) {
    return {
      label: "Clipper friendly",
      tone: "clipper",
    };
  }

  if (["youtube"].includes(normalized)) {
    return {
      label: "YouTube",
      tone: "content",
    };
  }

    if (["irl"].includes(normalized)) {
    return {
      label: "IRL",
      tone: "content",
    };
  }

  return {
    label: "Game category",
    tone: "game",
  };
}

function CategoryCard({ category }) {
  const categorySlug = getCategorySlug(category);
  const badge = getCategoryBadge(category.categoryName);
  const badgeStyle =
    badge.tone === "clipper"
      ? { backgroundColor: "#050505", color: "#84ff00", border: "1px solid rgba(132,255,0,0.55)" }
      : badge.tone === "content"
        ? { backgroundColor: "#050505", color: "#d8b4fe", border: "1px solid rgba(168,85,247,0.55)" }
        : { backgroundColor: "#050505", color: "#f4f4f5", border: "1px solid rgba(255,255,255,0.3)" };

  return (
    <Link
      to={`/editors/${categorySlug || category.id}`}
      className="group relative overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1"
      style={{
        borderColor: HAIRLINE,
        backgroundColor: SURFACE,
      }}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={category.categoryImage}
          alt={category.categoryName}
          fetchPriority="high"
          decoding="async"
          loading="eager"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />

        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.7) 100%)",
          }}
        />

        <div className="absolute left-3 top-3">
          <span
            className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
            style={{
              backgroundColor: MINT,
              color: "#0a0f0d",
            }}
          >
            {formatEditorsCount(category.numberOfEditors)} Profiles
          </span>
        </div>

        <div className="absolute right-3 top-3">
          <span
            className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
            style={badgeStyle}
          >
            {badge.label}
          </span>
        </div>
      </div>

      {/* Bottom content */}
      <div className="p-5">
        <h2 className="text-lg font-semibold text-white">
          {category.categoryName}
        </h2>
        <p className="mt-2 text-xs leading-relaxed text-zinc-500">
          {badge.tone === "clipper"
            ? "Best starting point for stream clipping campaigns and short form work."
            : badge.tone === "content"
              ? "Useful for creators hiring around a format instead of one game."
              : "Find editors and clippers who understand the game and audience."}
        </p>
        <div
          className="mt-4 text-sm font-medium transition-colors group-hover:text-white"
          style={{ color: PURPLE }}
        >
          Browse editors and clippers
        </div>
      </div>
    </Link>
  );
}

export default function GameOfChoice() {
  const [categories, setCategories] = useState([]);
  const [searchItem, setSearchItem] = useState("");

  useEffect(() => {
    const fetchCategoriesWithEditors = async () => {
      try {
        const [catsSnap, editors] = await Promise.all([
          getDocs(collection(db, "Categories")),
          listPublicProfiles({ role: "Editor" }),
        ]);

        const cats = catsSnap.docs.map((catDoc) => {
            const { categoryName, categoryImage } = catDoc.data();
            const normalizedCategory = String(categoryName || "").trim().toLowerCase();
            const matchingEditors = editors.filter((editor) => {
              const mainGame = typeof editor.profileInfo?.mainGame === "object"
                ? editor.profileInfo.mainGame.categoryName || editor.profileInfo.mainGame.name
                : editor.profileInfo?.mainGame;
              return editor.profileInfo?.availableForAll === true ||
                String(mainGame || "").trim().toLowerCase() === normalizedCategory;
            });

            return {
              id: catDoc.id,
              categoryName,
              categoryImage,
              numberOfEditors: matchingEditors.length,
            };
          });

        cats.sort((a, b) => {
          const priority = ["TikTok clips", "YouTube", "IRL"];
          const aPriority = priority.indexOf(a.categoryName);
          const bPriority = priority.indexOf(b.categoryName);
          if (aPriority !== -1 || bPriority !== -1) {
            return (aPriority === -1 ? 99 : aPriority) - (bPriority === -1 ? 99 : bPriority);
          }
          return b.numberOfEditors - a.numberOfEditors;
        });
        setCategories(cats);
      } catch (err) {
        console.error("Failed to fetch categories", err);
      }
    };

    fetchCategoriesWithEditors();
  }, []);

  const handleInputChange = (e) => {
    setSearchItem(e.target.value);
  };

  const filteredCategories = categories.filter((category) =>
    category.categoryName
      .toLowerCase()
      .includes(searchItem.toLowerCase())
  );

  return (
    <>
      <SEO
        title="Gaming Video Editors and Clippers by Category | Kre8"
        description="Find gaming video editors and clippers by content category, including TikTok clips, YouTube, IRL, Marvel Rivals, Rainbow Six Siege, Call of Duty, Apex Legends, League of Legends, Valorant, and Fortnite."
        canonicalPath="/gaming-video-editors"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Gaming Video Editors and Clippers by Category",
            description:
              "Find gaming video editors and clippers by content category, including TikTok clips, YouTube, IRL, Marvel Rivals, Rainbow Six Siege, Call of Duty, Apex Legends, League of Legends, Valorant, and Fortnite.",
            url: `${SITE_URL}/gaming-video-editors`,
          },
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Gaming Video Editors and Clippers", path: "/gaming-video-editors" },
          ]),
        ]}
      />

      <TheNav />

      <main
        className="mobile-safe min-h-screen px-4 py-10 sm:px-6 sm:py-16"
        style={{
          backgroundColor: BG,
          color: "#e5e7eb",
        }}
      >
        <div className="mx-auto max-w-7xl">
          {/* HERO */}
          <div className="text-center">
            <p
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: MINT }}
            >
              Content categories for editors and clippers
            </p>

            <h1 className="mt-3 text-3xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
              Gaming Video Editors and Clippers by Category
            </h1>

            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
              Start with TikTok clips for clipping campaigns, or browse by game
              when you need editors and clippers who understand a specific
              Twitch audience.
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-2xl">
            <div className="relative">
              {/* <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-500" /> */}
              <input
                type="text"
                value={DOMPurify.sanitize(searchItem)}
                onChange={handleInputChange}
                className="w-full rounded-2xl border py-4 pl-12 pr-4 text-white outline-none transition-all focus:border-white/20"
                style={{
                  backgroundColor: SURFACE,
                  borderColor: HAIRLINE,
                }}
              />
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredCategories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
              />
            ))}
          </div>

          {filteredCategories.length === 0 && (
            <div className="mt-20 text-center">
              <p className="text-zinc-500">
                No categories found.
              </p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
